import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios'
import { fetchAuthState } from '@/services/authClient'

interface HttpConfig {
  serviceName?: string
  baseURL?: string
}

interface QueueItem {
  resolve: (token: string) => void
  reject: (error: unknown) => void
}

class HttpClient {
  private instance: AxiosInstance
  private config: HttpConfig
  private isRefreshing = false
  private failedQueue: QueueItem[] = []

  constructor(config: HttpConfig = {}) {
    this.config = config
    this.instance = axios.create({
      baseURL: config.baseURL,
    })

    this.setupInterceptors()
  }

  private processQueue(token: string | null, error?: unknown) {
    this.failedQueue.forEach((promise) => {
      if (token) {
        promise.resolve(token)
      } else {
        promise.reject(error)
      }
    })
    this.failedQueue = []
  }

  private async refreshToken(): Promise<string | null> {
    try {
      const authState = await fetchAuthState()
      if (!authState.jwt) {
        window.dispatchEvent(new CustomEvent('auth:session-expired'))
      }
      return authState.jwt
    } catch (error) {
      console.error('[HTTP] Token refresh failed:', error)
      window.dispatchEvent(new CustomEvent('auth:session-expired'))
      return null
    }
  }

  private setupInterceptors() {
    this.instance.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        console.error(`[HTTP] ${this.config.serviceName ?? ''} ${error.message}`)

        const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean }

        if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
          if (this.isRefreshing) {
            return new Promise((resolve, reject) => {
              this.failedQueue.push({
                resolve: (token: string) => {
                  originalRequest.headers.Authorization = `Bearer ${token}`
                  resolve(this.instance(originalRequest))
                },
                reject,
              })
            })
          }

          originalRequest._retry = true
          this.isRefreshing = true

          try {
            const newToken = await this.refreshToken()

            if (newToken) {
              this.setToken(`Bearer ${newToken}`)
              this.processQueue(newToken)
              window.dispatchEvent(new CustomEvent('auth:token-refreshed', { detail: newToken }))
              originalRequest.headers.Authorization = `Bearer ${newToken}`
              return this.instance(originalRequest)
            }

            // Session expired: reject queued requests so their promises settle.
            this.processQueue(null, error)
          } catch (refreshError) {
            this.processQueue(null, refreshError)
          } finally {
            this.isRefreshing = false
          }
        }

        return Promise.reject(error)
      }
    )
  }

  setToken(token: string | null) {
    if (token) {
      this.instance.defaults.headers.common['Authorization'] = token
    } else {
      delete this.instance.defaults.headers.common['Authorization']
    }
  }

  async get<T>(route: string, params?: Record<string, unknown>): Promise<T> {
    const res = await this.instance.get<T>(route, { params })
    return res.data
  }

  async post<T>(route: string, data?: unknown, params?: Record<string, unknown>): Promise<T> {
    const res = await this.instance.post<T>(route, data, { params })
    return res.data
  }

  async put<T>(route: string, data?: unknown, params?: Record<string, unknown>): Promise<T> {
    const res = await this.instance.put<T>(route, data, { params })
    return res.data
  }

  async delete<T>(route: string, params?: Record<string, unknown>): Promise<T> {
    const res = await this.instance.delete<T>(route, { params })
    return res.data
  }
}

export const api = new HttpClient({
  serviceName: 'EventAPI',
  baseURL: import.meta.env.VITE_API_URL,
})
